/**
 * API Service (TypeScript)
 *
 * Centralized API layer using the axios instance.
 * All API endpoints are organized by feature/domain.
 *
 * This service is aligned with the OpenAPI specification at:
 * http://localhost:8283/lyt-store/api-docs
 *
 * Usage:
 * - Import this service in store modules or components
 * - Call API methods which return promises
 * - All responses are automatically unwrapped from axios response objects
 *
 * Note: Never call API methods directly from components.
 * Always use store actions to make API calls.
 */

import type { AxiosResponse } from 'axios'
import http from './http'
import type {
  ApiResponse,
  PageResponse,
  AuthResponse,
  SignupRequest,
  UserDto,
  ProfileUpdateRequest,
  ShippingAddressDto,
  UserPreferencesDto,
  ProductAlertDto,
  CreateProductAlertRequest,
  Product,
  StoreDetailDto,
  Tag,
  Category,
  StoreCart,
  CartItemRequest,
  QuantityUpdateRequest,
  CartProcessRequest,
  OrderDto,
  OrderParams,
  OrderTrackingDto,
  DeliveryQuote,
  OrderCancellationRequest,
  OrderActionResponse,
  OrderManagementResponse,
  SelectPaymentRequest,
  FavoriteProductDto,
  FavoriteStoreDto,
  AddFavoriteProductRequest,
  FavoriteParams,
  ReviewDto,
  ReviewSummaryDto,
  AddReviewRequest,
  ReviewParams,
  SubscribeToNewsletterRequest,
  UnsubscribeFromNewsletterRequest,
  ProductDetails,
  ProductInstructions,
  ProductCriteria,
  StoreSearchCriteria,
  StoreFollowRequest,
  StoreFollowPreferencesRequest,
  NewsletterSubscriptionDto,
  RecentlyViewedProduct,
  // Geocoding Types
  GeoSearchResult,
  ReverseGeocodeResult,
  RouteDirectionsDto,
  MapTileConfigDto,
  // Delivery System Types (Phase 5.1)
  DeliveryAvailabilityResponse,
  DeliveryTrackingDto,
  DeliveryCancelRequest,
  DeliveryCancelResponse,
  DeliveryRetryResponse,
  DeliveryStatsDto,
  StoreDeliveryDto,
  PickupLocationUpdateRequest,
  DeliveryFilterOptions,
  // Dev Payment Simulation Types
  BulkSimulatePaymentResponse,
  // Onboarding Types
  EcommerceOnboardingSummary,
} from '../types'
import type { KazangPaymentRequest, KazangPaymentResponse } from '../types/payment'
import type {
  PrescriptionRequestDto,
  PrescriptionDetailDto,
  PrescriptionMessageDto,
  PrescriptionAttachmentDto,
  CreatePrescriptionRequest,
  PrescriptionCheckoutRequest,
  PrescriptionPrepareRequest,
  AdminPrescriptionListParams,
  PreparedItemDto,
  CreatePreparedItemRequest,
  UpdatePreparedItemRequest,
  AdminProductListResponseDto,
  AdminProductSearchParams,
} from '@/stores/types/prescription.types'
import type { AdminSettingsDto, AdminAssetUploadResponse } from '@/stores/types/admin.settings.types'

/**
 * Unwrap axios response to get just the data
 * @param p - Promise from axios
 * @returns Promise that resolves to response.data
 */
const unwrap = <T>(p: Promise<AxiosResponse<T>>): Promise<T> =>
  p.then((r) => r.data)

export const api = {
  /**
   * Authentication endpoints
   * Path: /v1/auth/*
   */
  auth: {
    /**
     * Login user with email and password
     */
    login: (email: string, password: string): Promise<AuthResponse> =>
      unwrap<AuthResponse>(http.post('/v1/auth/login', { email, password })),

    /**
     * Register new user
     */
    register: (payload: SignupRequest): Promise<AuthResponse> =>
      unwrap<AuthResponse>(http.post('/v1/auth/register', payload)),

    /**
     * Logout current user
     */
    logout: (): Promise<void> =>
      unwrap<void>(http.post('/v1/auth/logout')),

    /**
     * Refresh JWT access token
     */
    refresh: (refreshToken: string): Promise<AuthResponse> =>
      unwrap<AuthResponse>(http.post('/v1/auth/refresh', { refreshToken })),

    /**
     * Verify email address
     */
    verifyEmail: (token?: string): Promise<{ message: string }> =>
      unwrap<{ message: string }>(http.get('/v1/auth/verify', token ? { params: { token } } : undefined)),

    /**
     * Request a one-time passcode for passwordless login
     */
    requestOtp: (email?: string, phone?: string): Promise<{ message?: string }> =>
      unwrap<{ message?: string }>(http.post('/v1/auth/otp/request', { email, phone })),

    /**
     * Verify a one-time passcode and issue tokens
     */
    verifyOtp: (email?: string, otp?: string, phone?: string): Promise<AuthResponse> =>
      unwrap<AuthResponse>(http.post('/v1/auth/otp/verify', { email, otp, phone })),

    /**
     * Send a magic link for passwordless login
     */
    sendMagicLink: (email?: string, phone?: string): Promise<{ message?: string }> =>
      unwrap<{ message?: string }>(http.post('/v1/auth/magic-link', { email, phone })),
  },

  /**
   * User profile and preferences endpoints
   * Path: /v1/me/*
   */
  user: {
    /**
     * Get current user profile
     */
    me: (): Promise<ApiResponse<UserDto>> =>
      unwrap<ApiResponse<UserDto>>(http.get('/v1/me')),

    /**
     * Update user profile
     */
    update: (payload: ProfileUpdateRequest): Promise<ApiResponse<UserDto>> =>
      unwrap<ApiResponse<UserDto>>(http.put('/v1/me', payload)),

    /**
     * Upload user avatar/profile picture
     */
    uploadAvatar: (file: File): Promise<ApiResponse<{ avatarUrl: string }>> => {
      const form = new FormData()
      form.append('avatar', file)
      return unwrap<ApiResponse<{ avatarUrl: string }>>(
        http.post('/v1/me/avatar', form, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
      )
    },

    /**
     * Delete current user account
     */
    deleteAccount: (): Promise<ApiResponse<string>> =>
      unwrap<ApiResponse<string>>(http.delete('/v1/me')),

    /**
     * Address management
     */
    addresses: {
      /**
       * List all user addresses
       */
      list: (): Promise<ApiResponse<ShippingAddressDto[]>> =>
        unwrap<ApiResponse<ShippingAddressDto[]>>(http.get('/me/addresses')),

      /**
       * Create new address
       */
      create: (payload: Omit<ShippingAddressDto, 'id'>): Promise<ApiResponse<ShippingAddressDto>> =>
        unwrap<ApiResponse<ShippingAddressDto>>(http.post('/me/addresses', payload)),

      /**
       * Update existing address
       */
      update: (id: string, payload: Partial<Omit<ShippingAddressDto, 'id'>>): Promise<ApiResponse<ShippingAddressDto>> =>
        unwrap<ApiResponse<ShippingAddressDto>>(http.put(`/me/addresses/${id}`, payload)),

      /**
       * Delete address
       */
      remove: (id: string): Promise<ApiResponse<void>> =>
        unwrap<ApiResponse<void>>(http.delete(`/me/addresses/${id}`)),

      /**
       * Set address as primary
       */
      setPrimary: (id: string): Promise<ApiResponse<ShippingAddressDto>> =>
        unwrap<ApiResponse<ShippingAddressDto>>(http.put(`/me/addresses/${id}/primary`)),

      /**
       * Get primary address
       */
      getPrimary: (): Promise<ApiResponse<ShippingAddressDto>> =>
        unwrap<ApiResponse<ShippingAddressDto>>(http.get('/me/addresses/primary')),
    },

    /**
     * User preferences
     */
    preferences: {
      /**
       * Get user preferences
       */
      get: (): Promise<ApiResponse<UserPreferencesDto>> =>
        unwrap<ApiResponse<UserPreferencesDto>>(http.get('/v1/me/preferences')),

      /**
       * Update user preferences
       */
      update: (payload: Partial<UserPreferencesDto>): Promise<ApiResponse<UserPreferencesDto>> =>
        unwrap<ApiResponse<UserPreferencesDto>>(http.put('/v1/me/preferences', payload)),
    },

    /**
     * Product alerts (price drops, back in stock)
     */
    productAlerts: {
      /**
       * List all product alerts
       */
      list: (): Promise<PageResponse<ProductAlertDto>> =>
        unwrap<PageResponse<ProductAlertDto>>(http.get('/v1/me/product-alerts')),

      /**
       * Create new product alert
       */
      create: (payload: CreateProductAlertRequest): Promise<ApiResponse<ProductAlertDto>> =>
        unwrap<ApiResponse<ProductAlertDto>>(http.post('/v1/me/product-alerts', payload)),

      /**
       * Delete product alert
       */
      remove: (id: string): Promise<ApiResponse<void>> =>
        unwrap<ApiResponse<void>>(http.delete(`/v1/me/product-alerts/${id}`)),
    },

  },

  /**
   * Stores endpoints
   * Path: /v1/stores/*
   */
  stores: {
    /**
     * Search and browse stores
     */
    search: (params: StoreSearchCriteria): Promise<PageResponse<StoreDetailDto>> =>
      unwrap<PageResponse<StoreDetailDto>>(http.get('/v1/stores', { params })),

    /**
     * Get store details by slug
     */
    get: (slug: string): Promise<ApiResponse<StoreDetailDto>> =>
      unwrap<ApiResponse<StoreDetailDto>>(http.get(`/v1/stores/${slug}/details`)),

    /**
     * Follow a store (with notification preferences)
     */
    follow: (storeId: string, payload: StoreFollowRequest = {}): Promise<ApiResponse<{ message: string }>> =>
      unwrap<ApiResponse<{ message: string }>>(http.post(`/v1/followers/stores/${storeId}`, payload)),

    /**
     * Unfollow a store
     */
    unfollow: (storeId: string): Promise<ApiResponse<void>> =>
      unwrap<ApiResponse<void>>(http.delete(`/v1/followers/stores/${storeId}`)),

    /**
     * Update store follow preferences
     */
    setFollowPreferences: (storeId: string, payload: StoreFollowPreferencesRequest): Promise<ApiResponse<{ message: string }>> =>
      unwrap<ApiResponse<{ message: string }>>(http.put(`/v1/followers/stores/${storeId}/preferences`, payload)),

    /**
     * Add store to favorites/bookmarks
     */
    favorite: (storeId: string): Promise<ApiResponse<{ message: string }>> =>
      unwrap<ApiResponse<{ message: string }>>(http.post(`/v1/favorites/stores/${storeId}`)),

    /**
     * Remove store from favorites
     */
    unfavorite: (storeId: string): Promise<ApiResponse<void>> =>
      unwrap<ApiResponse<void>>(http.delete(`/v1/favorites/stores/${storeId}`)),

    /**
     * Get all tags
     */
    tags: (q: string = ''): Promise<PageResponse<Tag>> =>
      unwrap<PageResponse<Tag>>(http.get('/v1/tags', { params: { q } })),

    /**
     * Get active tags only
     */
    activeTags: (q: string = ''): Promise<PageResponse<Tag>> =>
      unwrap<PageResponse<Tag>>(http.get('/v1/tags/active', { params: { q } })),

    /**
     * Get store categories
     */
    categories: (slug: string, params: Record<string, string | number | boolean> = {}): Promise<ApiResponse<Category[]>> =>
      unwrap<ApiResponse<Category[]>>(http.get(`/v1/store/${slug}/category`, { params })),

    /**
     * Get main category details
     */
    categoriesMain: (slug: string, main: string): Promise<ApiResponse<Category>> =>
      unwrap<ApiResponse<Category>>(http.get(`/v1/store/${slug}/category/${main}`)),

    /**
     * Get subcategory details
     */
    categoriesSub: (slug: string, main: string, sub: string): Promise<ApiResponse<Category>> =>
      unwrap<ApiResponse<Category>>(http.get(`/v1/store/${slug}/category/${main}/${sub}`)),
  },

  /**
   * Products endpoints
   * Path: /v1/store/{slug}/products/*
   */
  products: {
    /**
     * Search products globally
     */
    search: (params: ProductCriteria): Promise<PageResponse<Product>> =>
      unwrap<PageResponse<Product>>(http.get('/v1/products', { params })),

    /**
     * Get products for a specific store
     */
    byStore: (slug: string, params: ProductCriteria): Promise<PageResponse<Product>> =>
      unwrap<PageResponse<Product>>(http.get(`/v1/store/${slug}/products`, { params })),

    /**
     * Get product details
     */
    get: (slugOrObj: string | { slug: string; plu: string }, plu?: string): Promise<ApiResponse<ProductDetails>> => {
      const slug = typeof slugOrObj === 'object' ? slugOrObj.slug : slugOrObj
      const productPlu = typeof slugOrObj === 'object' ? slugOrObj.plu : plu
      return unwrap<ApiResponse<ProductDetails>>(http.get(`/v1/store/${slug}/products/${productPlu}`))
    },

    /**
     * Get product options/instructions
     */
    options: (slug: string, plu: string): Promise<ApiResponse<ProductInstructions>> =>
      unwrap<ApiResponse<ProductInstructions>>(http.get(`/v1/store/${slug}/products/${plu}/options`)),

    /**
     * Get fastline (quick-order) products
     */
    fastline: (slug: string, params: ProductCriteria): Promise<PageResponse<Product>> =>
      unwrap<PageResponse<Product>>(http.get(`/v1/store/${slug}/products/fastline`, { params })),
  },

  /**
   * Shopping cart endpoints
   * Path: /v1/cart/*
   */
  cart: {
    /**
     * Get all carts (summary across all stores)
     */
    getAll: (): Promise<ApiResponse<StoreCart[]>> =>
      unwrap<ApiResponse<StoreCart[]>>(http.get('/v1/cart')),

    /**
     * Get cart for specific store
     */
    get: (storeSlug: string): Promise<ApiResponse<StoreCart>> =>
      unwrap<ApiResponse<StoreCart>>(http.get(`/v1/cart/${storeSlug}`)),

    /**
     * Add item to cart
     */
    add: (storeSlug: string, payload: CartItemRequest): Promise<ApiResponse<StoreCart>> =>
      unwrap<ApiResponse<StoreCart>>(http.post(`/v1/cart/${storeSlug}/items`, payload)),

    /**
     * Update cart item quantity
     */
    update: (storeSlug: string, plu: string, payload: QuantityUpdateRequest): Promise<ApiResponse<StoreCart>> =>
      unwrap<ApiResponse<StoreCart>>(http.put(`/v1/cart/${storeSlug}/items/${plu}`, payload)),

    /**
     * Remove item from cart
     */
    remove: (storeSlug: string, plu: string): Promise<ApiResponse<StoreCart>> =>
      unwrap<ApiResponse<StoreCart>>(http.delete(`/v1/cart/${storeSlug}/items/${plu}`)),

    /**
     * Clear entire cart for a store
     */
    clear: (storeSlug: string): Promise<ApiResponse<StoreCart>> =>
      unwrap<ApiResponse<StoreCart>>(http.delete(`/v1/cart/${storeSlug}`)),

    /**
     * Replace all items in a store cart (atomic)
     */
    replace: (storeSlug: string, items: CartItemRequest[]): Promise<ApiResponse<StoreCart>> =>
      unwrap<ApiResponse<StoreCart>>(http.put(`/v1/cart/${storeSlug}`, { items })),

    /**
     * Process cart (create order)
     */
    process: (storeSlug: string, payload: CartProcessRequest): Promise<ApiResponse<OrderDto>> =>
      unwrap<ApiResponse<OrderDto>>(http.post(`/v1/cart/${storeSlug}/process`, payload)),
  },

  /**
   * Orders endpoints
   * Path: /v1/orders/*
   */
  orders: {
    /**
     * List user orders with filtering and pagination
     */
    list: (params: OrderParams): Promise<PageResponse<OrderDto>> =>
      unwrap<PageResponse<OrderDto>>(http.get('/v1/orders', { params })),

    /**
     * Get order details
     */
    get: (id: string): Promise<ApiResponse<OrderDto>> =>
      unwrap<ApiResponse<OrderDto>>(http.get(`/v1/orders/${id}`)),

    /**
     * Get order tracking information with status timeline
     */
    tracking: (id: string): Promise<ApiResponse<OrderTrackingDto>> =>
      unwrap<ApiResponse<OrderTrackingDto>>(http.get(`/v1/orders/${id}/tracking`)),

    /**
     * Get delivery fee quote for a selected address
     */
    deliveryQuote: (storeSlug: string, shippingAddressId: string): Promise<ApiResponse<DeliveryQuote>> =>
      unwrap<ApiResponse<DeliveryQuote>>(http.get('/v1/orders/delivery-quote', {
        params: { storeSlug, shippingAddressId }
      })),

    /**
     * Check if order can be cancelled
     */
    canCancel: (id: string): Promise<ApiResponse<boolean>> =>
      unwrap<ApiResponse<boolean>>(http.get(`/v1/orders/${id}/can-cancel`)),

    /**
     * Cancel an order
     */
    cancel: (id: string, request: OrderCancellationRequest): Promise<ApiResponse<OrderActionResponse>> =>
      unwrap<ApiResponse<OrderActionResponse>>(http.post(`/v1/orders/${id}/cancel`, request)),

    /**
     * Accept changes on a pending adjusted order (customer-triggered)
     */
    acceptChanges: (id: string): Promise<ApiResponse<OrderManagementResponse>> =>
      unwrap<ApiResponse<OrderManagementResponse>>(http.post(`/v1/orders/${id}/accept-changes`)),

    /**
      * Select payment method after store approval
      */
    selectPayment: (id: string, payload: SelectPaymentRequest): Promise<ApiResponse<OrderDto>> =>
      unwrap<ApiResponse<OrderDto>>(http.post(`/v1/orders/${id}/payment`, payload)),

    /**
     * Process KAZANG mobile money payment
     */
    processKazangPayment: (orderId: string, payload: KazangPaymentRequest): Promise<ApiResponse<KazangPaymentResponse>> =>
      unwrap<ApiResponse<KazangPaymentResponse>>(http.post(`/v1/orders/${orderId}/payments/kazang`, payload)),

    /**
     * Dev-only: Simulate Kazang payment for an order
     * Only available when backend dev-tools are enabled
     */
    simulatePayment: (orderId: string, phoneNumber: string): Promise<ApiResponse<OrderDto>> =>
      unwrap<ApiResponse<OrderDto>>(http.post(`/v1/orders/${orderId}/dev/simulate-payment`, { phoneNumber })),

    /**
     * Dev-only: Simulate Kazang payment for multiple orders at once
     * Only available when backend dev-tools are enabled
     */
    simulatePaymentBulk: (orderIds: string[], phoneNumber: string): Promise<ApiResponse<BulkSimulatePaymentResponse>> =>
      unwrap<ApiResponse<BulkSimulatePaymentResponse>>(http.post('/v1/orders/dev/simulate-payment/bulk', { orderIds, phoneNumber })),
  },

  /**
   * Favorites/Wishlist endpoints
   * Path: /v1/favorites/*
   */
  favorites: {
    /**
     * Product favorites (wishlist)
     */
    products: {
      /**
       * List favorite products
       */
      list: (params: FavoriteParams): Promise<PageResponse<FavoriteProductDto>> =>
        unwrap<PageResponse<FavoriteProductDto>>(http.get('/v1/favorites/products', { params })),

      /**
       * Get favorites count
       */
      count: (): Promise<ApiResponse<{ count: number }>> =>
        unwrap<ApiResponse<{ count: number }>>(http.get('/v1/favorites/products/count')),

      /**
       * Add product to favorites
       */
      add: (payload: AddFavoriteProductRequest): Promise<ApiResponse<FavoriteProductDto>> =>
        unwrap<ApiResponse<FavoriteProductDto>>(http.post('/v1/favorites/products', payload)),

      /**
       * Remove product from favorites by favorite ID
       */
      remove: (favoriteId: string): Promise<ApiResponse<void>> =>
        unwrap<ApiResponse<void>>(http.delete(`/v1/favorites/products/${favoriteId}`)),

      /**
       * Remove product from favorites by PLU
       */
      removeByPlu: (storeId: string, productPlu: string): Promise<ApiResponse<void>> =>
        unwrap<ApiResponse<void>>(http.delete('/v1/favorites/products/by-plu', {
          params: { storeId, productPlu }
        })),
    },

    /**
     * Store favorites (quick access bookmarks)
     */
    stores: {
      /**
       * List favorite stores
       */
      list: (params?: FavoriteParams): Promise<PageResponse<FavoriteStoreDto>> =>
        unwrap<PageResponse<FavoriteStoreDto>>(http.get('/v1/favorites/stores', { params })),

      /**
       * Get favorite stores count
       */
      count: (): Promise<ApiResponse<{ count: number }>> =>
        unwrap<ApiResponse<{ count: number }>>(http.get('/v1/favorites/stores/count')),
    },
  },

  /**
   * Reviews endpoints
   * Path: /stores/{storeId}/reviews/*
   */
  reviews: {
    /**
     * Get store reviews
     */
    forStore: (storeId: string, params: ReviewParams): Promise<PageResponse<ReviewDto>> =>
      unwrap<PageResponse<ReviewDto>>(http.get(`/stores/${storeId}/reviews`, { params })),

    /**
     * Get store review summary
     */
    summaryForStore: (storeId: string): Promise<ApiResponse<ReviewSummaryDto>> =>
      unwrap<ApiResponse<ReviewSummaryDto>>(http.get(`/stores/${storeId}/reviews/summary`)),

    /**
     * Get product reviews
     */
    forProduct: (storeId: string, plu: string, params: ReviewParams): Promise<PageResponse<ReviewDto>> =>
      unwrap<PageResponse<ReviewDto>>(http.get(`/stores/${storeId}/products/${plu}/reviews`, { params })),

    /**
     * Get product review summary
     */
    summaryForProduct: (storeId: string, plu: string): Promise<ApiResponse<ReviewSummaryDto>> =>
      unwrap<ApiResponse<ReviewSummaryDto>>(http.get(`/stores/${storeId}/products/${plu}/reviews/summary`)),

    /**
     * Create store review
     */
    createForStore: (storeId: string, payload: AddReviewRequest): Promise<ApiResponse<ReviewDto>> =>
      unwrap<ApiResponse<ReviewDto>>(http.post(`/stores/${storeId}/reviews`, payload)),

    /**
     * Create product review
     */
    createForProduct: (storeId: string, plu: string, payload: AddReviewRequest): Promise<ApiResponse<ReviewDto>> =>
      unwrap<ApiResponse<ReviewDto>>(http.post(`/stores/${storeId}/products/${plu}/reviews`, payload)),
  },

  /**
   * Newsletter endpoints
   * Path: /api/v1/newsletter/*
   */
  newsletter: {
    /**
     * Subscribe to newsletter
     */
    subscribe: (payload: SubscribeToNewsletterRequest): Promise<ApiResponse<NewsletterSubscriptionDto>> =>
      unwrap<ApiResponse<NewsletterSubscriptionDto>>(http.post('/api/v1/newsletter/subscribe', payload)),

    /**
     * Unsubscribe from newsletter
     */
    unsubscribe: (payload: UnsubscribeFromNewsletterRequest): Promise<ApiResponse<{ message: string }>> =>
      unwrap<ApiResponse<{ message: string }>>(http.post('/api/v1/newsletter/unsubscribe', payload)),
  },

  /**
   * Recently viewed products endpoints
   * Path: /v1/recently-viewed/*
   * Hybrid auth: works for both authenticated users and anonymous sessions
   */
  recentlyViewed: {
    /**
     * List recently viewed products
     */
    list: (limit: number = 20): Promise<ApiResponse<RecentlyViewedProduct[]>> =>
      unwrap<ApiResponse<RecentlyViewedProduct[]>>(http.get('/v1/recently-viewed', { params: { limit } })),

    /**
     * Remove a single recently viewed item
     */
    remove: (id: string): Promise<ApiResponse<string>> =>
      unwrap<ApiResponse<string>>(http.delete(`/v1/recently-viewed/${id}`)),

    /**
     * Clear all recently viewed history
     */
    clearAll: (): Promise<ApiResponse<string>> =>
      unwrap<ApiResponse<string>>(http.delete('/v1/recently-viewed')),
  },

  // ============================================================================
  // Geocoding & Map API
  // ============================================================================

  /**
   * Geocoding endpoints (proxied through backend)
   * Path: /v1/geo/*
   */
  geo: {
    /**
     * Search for addresses within Lusaka Province
     */
    search: (q: string, limit = 5): Promise<ApiResponse<GeoSearchResult[]>> =>
      unwrap<ApiResponse<GeoSearchResult[]>>(http.get('/v1/geo/search', { params: { q, limit } })),

    /**
     * Autocomplete address search
     */
    autocomplete: (q: string, limit = 5): Promise<ApiResponse<GeoSearchResult[]>> =>
      unwrap<ApiResponse<GeoSearchResult[]>>(http.get('/v1/geo/autocomplete', { params: { q, limit } })),

    /**
     * Reverse geocode coordinates to address
     */
    reverse: (lat: number, lng: number): Promise<ApiResponse<ReverseGeocodeResult>> =>
      unwrap<ApiResponse<ReverseGeocodeResult>>(http.get('/v1/geo/reverse', { params: { lat, lng } })),

    /**
     * Get driving route between two points
     */
    route: (originLat: number, originLng: number, destLat: number, destLng: number): Promise<ApiResponse<RouteDirectionsDto>> =>
      unwrap<ApiResponse<RouteDirectionsDto>>(http.get('/v1/geo/route', { params: { originLat, originLng, destLat, destLng } })),

    /**
     * Get map tile configuration (public, no auth required)
     */
    mapConfig: (): Promise<ApiResponse<MapTileConfigDto>> =>
      unwrap<ApiResponse<MapTileConfigDto>>(http.get('/v1/geo/map-config')),
  },

  // ============================================================================
  // Delivery System API (Phase 5.1)
  // ============================================================================

  /**
   * Customer-facing delivery endpoints
   * Path: /v1/delivery/*
   */
  delivery: {
    /**
     * Check if delivery is available for a store and shipping address
     */
    checkAvailability: (
      storeSlug: string,
      shippingAddressId: string
    ): Promise<ApiResponse<DeliveryAvailabilityResponse>> =>
      unwrap<ApiResponse<DeliveryAvailabilityResponse>>(
        http.get('/v1/delivery/availability', {
          params: { storeSlug, shippingAddressId }
        })
      ),

    /**
     * Get detailed delivery tracking for an order
     */
    getTracking: (orderId: string): Promise<ApiResponse<DeliveryTrackingDto>> =>
      unwrap<ApiResponse<DeliveryTrackingDto>>(http.get(`/v1/delivery/${orderId}/tracking`)),

    /**
     * Get SSE stream URL for real-time delivery tracking
     * Returns the URL to connect to for EventSource
     */
    getTrackingStreamUrl: (orderId: string): string => {
      const baseUrl = http.defaults.baseURL || ''
      return `${baseUrl}/v1/delivery/tracking/${orderId}/stream`
    },

    /**
     * Cancel a delivery (customer-initiated)
     */
    cancelDelivery: (
      orderId: string,
      request: DeliveryCancelRequest
    ): Promise<ApiResponse<DeliveryCancelResponse>> =>
      unwrap<ApiResponse<DeliveryCancelResponse>>(
        http.post(`/v1/delivery/${orderId}/cancel`, request)
      ),
  },

  /**
   * Pharmacy (Rx) customer-facing endpoints
   * Path: /v1/pharmacy/*
   * Authenticated via JWT Bearer token
   */
  pharmacy: {
    prescriptions: {
      create: (payload: CreatePrescriptionRequest): Promise<ApiResponse<PrescriptionRequestDto>> =>
        unwrap<ApiResponse<PrescriptionRequestDto>>(http.post('/v1/pharmacy/prescriptions', payload)),

      uploadAttachment: (id: string, file: File): Promise<ApiResponse<PrescriptionAttachmentDto>> => {
        const form = new FormData()
        form.append('file', file)
        return unwrap<ApiResponse<PrescriptionAttachmentDto>>(
          http.post(`/v1/pharmacy/prescriptions/${id}/attachments`, form, {
            headers: { 'Content-Type': 'multipart/form-data' }
          })
        )
      },

      uploadAttachments: async (id: string, files: File[]): Promise<ApiResponse<PrescriptionAttachmentDto>[]> => {
        const results: ApiResponse<PrescriptionAttachmentDto>[] = []
        for (const file of files) {
          results.push(await api.pharmacy.prescriptions.uploadAttachment(id, file))
        }
        return results
      },

      list: (page = 1, size = 20): Promise<PageResponse<PrescriptionRequestDto>> =>
        unwrap<PageResponse<PrescriptionRequestDto>>(
          http.get('/v1/pharmacy/prescriptions', { params: { page, size } })
        ),

      getDetail: (id: string): Promise<ApiResponse<PrescriptionDetailDto>> =>
        unwrap<ApiResponse<PrescriptionDetailDto>>(http.get(`/v1/pharmacy/prescriptions/${id}`)),

      listMessages: (id: string, page = 1, size = 50): Promise<PageResponse<PrescriptionMessageDto>> =>
        unwrap<PageResponse<PrescriptionMessageDto>>(
          http.get(`/v1/pharmacy/prescriptions/${id}/messages`, { params: { page, size } })
        ),

      sendMessage: (id: string, payload: { message: string }): Promise<ApiResponse<PrescriptionMessageDto>> =>
        unwrap<ApiResponse<PrescriptionMessageDto>>(
          http.post(`/v1/pharmacy/prescriptions/${id}/messages`, payload)
        ),

      cancel: (id: string, payload?: { reason?: string }): Promise<ApiResponse<PrescriptionRequestDto>> =>
        unwrap<ApiResponse<PrescriptionRequestDto>>(
          http.post(`/v1/pharmacy/prescriptions/${id}/cancel`, payload || {})
        ),

      checkout: (id: string, payload: PrescriptionCheckoutRequest): Promise<ApiResponse<PrescriptionRequestDto>> =>
        unwrap<ApiResponse<PrescriptionRequestDto>>(
          http.post(`/v1/pharmacy/prescriptions/${id}/checkout`, payload)
        ),

      getSSEUrl: (id: string): string => {
        const baseUrl = http.defaults.baseURL || ''
        return `${baseUrl}/v1/pharmacy/prescriptions/${id}/events`
      },

      listPreparedItems: (requestId: string): Promise<ApiResponse<PreparedItemDto[]>> =>
        unwrap<ApiResponse<PreparedItemDto[]>>(
          http.get(`/v1/pharmacy/prescriptions/${requestId}/prepared-items`)
        ),
    },
  },

  /**
   * Admin delivery management endpoints (staff session cookie auth)
   * Path: /v1/admin/deliveries/*
   * All calls use withCredentials: true for HttpOnly cookie auth
   */
  admin: {
    orders: {
      /**
       * Get order details by ID (staff session cookie auth)
       */
      get: (orderId: string): Promise<ApiResponse<OrderDto>> =>
        unwrap<ApiResponse<OrderDto>>(
          http.get(`/v1/admin/orders/${orderId}`, { withCredentials: true })
        ),
    },

    deliveries: {
      /**
       * List deliveries for the staff member's store
       */
      list: (
        filters?: DeliveryFilterOptions
      ): Promise<PageResponse<StoreDeliveryDto>> =>
        unwrap<PageResponse<StoreDeliveryDto>>(
          http.get('/v1/admin/deliveries', { params: filters, withCredentials: true })
        ),

      /**
       * Get detailed delivery tracking for a specific order
       */
      getDetail: (
        orderId: string
      ): Promise<ApiResponse<DeliveryTrackingDto>> =>
        unwrap<ApiResponse<DeliveryTrackingDto>>(
          http.get(`/v1/admin/deliveries/${orderId}`, { withCredentials: true })
        ),

      /**
       * Get SSE stream URL for admin delivery tracking
       * Returns the URL to connect to for EventSource (cookie auth sent automatically)
       */
      getStreamUrl: (orderId: string): string => {
        const baseUrl = http.defaults.baseURL || ''
        return `${baseUrl}/v1/admin/deliveries/${orderId}/stream`
      },

      /**
       * Retry a failed delivery dispatch
       */
      retry: (
        orderId: string
      ): Promise<ApiResponse<DeliveryRetryResponse>> =>
        unwrap<ApiResponse<DeliveryRetryResponse>>(
          http.post(`/v1/admin/deliveries/${orderId}/retry`, {}, { withCredentials: true })
        ),

      /**
       * Re-dispatch a failed delivery — creates a fresh Unka order and resets retryCount to 0.
       * Unlike retry (which increments retryCount), re-dispatch starts completely fresh.
       */
      reDispatch: (
        orderId: string
      ): Promise<ApiResponse<DeliveryRetryResponse>> =>
        unwrap<ApiResponse<DeliveryRetryResponse>>(
          http.post(`/v1/admin/deliveries/${orderId}/re-dispatch`, {}, { withCredentials: true })
        ),

      /**
       * Initiate delivery dispatch for an order that was not dispatched
       */
      initiate: (
        orderId: string
      ): Promise<ApiResponse<DeliveryRetryResponse>> =>
        unwrap<ApiResponse<DeliveryRetryResponse>>(
          http.post(`/v1/admin/deliveries/${orderId}/initiate`, {}, { withCredentials: true })
        ),

      /**
       * Cancel a delivery (store-initiated via admin)
       */
      cancel: (
        orderId: string,
        request: DeliveryCancelRequest
      ): Promise<ApiResponse<DeliveryCancelResponse>> =>
        unwrap<ApiResponse<DeliveryCancelResponse>>(
          http.post(`/v1/admin/deliveries/${orderId}/cancel`, request, { withCredentials: true })
        ),

      /**
       * Get delivery statistics for the staff member's store
       */
      getStats: (
        startDate?: string,
        endDate?: string
      ): Promise<ApiResponse<DeliveryStatsDto>> =>
        unwrap<ApiResponse<DeliveryStatsDto>>(
          http.get('/v1/admin/deliveries/stats', {
            params: { startDate, endDate },
            withCredentials: true
          })
        ),

      /**
       * Update store pickup location for delivery drivers
       */
      updatePickupLocation: (
        location: PickupLocationUpdateRequest
      ): Promise<ApiResponse<PickupLocationUpdateRequest>> =>
        unwrap<ApiResponse<PickupLocationUpdateRequest>>(
          http.put('/v1/admin/deliveries/pickup-location', location, { withCredentials: true })
        ),

      /**
       * Force refresh delivery status from provider
       */
      forceRefresh: (
        orderId: string
      ): Promise<ApiResponse<DeliveryTrackingDto>> =>
        unwrap<ApiResponse<DeliveryTrackingDto>>(
          http.post(`/v1/admin/deliveries/${orderId}/force-refresh`, {}, { withCredentials: true })
        ),
    },

    /**
     * Admin pharmacy/prescription management endpoints (staff session cookie auth)
     * Path: /v1/admin/prescriptions/*
     * All calls use withCredentials: true for HttpOnly cookie auth
     */
    prescriptions: {
      list: (params?: AdminPrescriptionListParams): Promise<PageResponse<PrescriptionRequestDto>> =>
        unwrap<PageResponse<PrescriptionRequestDto>>(
          http.get('/v1/admin/prescriptions', { params, withCredentials: true })
        ),

      getDetail: (id: string): Promise<ApiResponse<PrescriptionDetailDto>> =>
        unwrap<ApiResponse<PrescriptionDetailDto>>(
          http.get(`/v1/admin/prescriptions/${id}`, { withCredentials: true })
        ),

      startReview: (id: string): Promise<ApiResponse<PrescriptionRequestDto>> =>
        unwrap<ApiResponse<PrescriptionRequestDto>>(
          http.post(`/v1/admin/prescriptions/${id}/start-review`, {}, { withCredentials: true })
        ),

      prepare: (id: string, payload: PrescriptionPrepareRequest): Promise<ApiResponse<PrescriptionRequestDto>> =>
        unwrap<ApiResponse<PrescriptionRequestDto>>(
          http.post(`/v1/admin/prescriptions/${id}/prepare`, payload, { withCredentials: true })
        ),

      reject: (id: string, payload: { reason: string }): Promise<ApiResponse<PrescriptionRequestDto>> =>
        unwrap<ApiResponse<PrescriptionRequestDto>>(
          http.post(`/v1/admin/prescriptions/${id}/reject`, payload, { withCredentials: true })
        ),

      dispense: (id: string): Promise<ApiResponse<PrescriptionRequestDto>> =>
        unwrap<ApiResponse<PrescriptionRequestDto>>(
          http.post(`/v1/admin/prescriptions/${id}/dispense`, {}, { withCredentials: true })
        ),

      listMessages: (id: string, page = 1, size = 50): Promise<PageResponse<PrescriptionMessageDto>> =>
        unwrap<PageResponse<PrescriptionMessageDto>>(
          http.get(`/v1/admin/prescriptions/${id}/messages`, { params: { page, size }, withCredentials: true })
        ),

      sendMessage: (id: string, payload: { message: string }): Promise<ApiResponse<PrescriptionMessageDto>> =>
        unwrap<ApiResponse<PrescriptionMessageDto>>(
          http.post(`/v1/admin/prescriptions/${id}/messages`, payload, { withCredentials: true })
        ),

      getSSEUrl: (id: string): string => {
        const baseUrl = http.defaults.baseURL || ''
        return `${baseUrl}/v1/admin/prescriptions/${id}/events`
      },

      listPreparedItems: (requestId: string): Promise<ApiResponse<PreparedItemDto[]>> =>
        unwrap<ApiResponse<PreparedItemDto[]>>(
          http.get(`/v1/admin/prescriptions/${requestId}/prepared-items`, { withCredentials: true })
        ),

      createPreparedItem: (
        requestId: string,
        payload: CreatePreparedItemRequest
      ): Promise<ApiResponse<PreparedItemDto>> =>
        unwrap<ApiResponse<PreparedItemDto>>(
          http.post(`/v1/admin/prescriptions/${requestId}/prepared-items`, payload, {
            withCredentials: true,
          })
        ),

      updatePreparedItem: (
        requestId: string,
        itemId: string,
        payload: UpdatePreparedItemRequest
      ): Promise<ApiResponse<PreparedItemDto>> =>
        unwrap<ApiResponse<PreparedItemDto>>(
          http.put(`/v1/admin/prescriptions/${requestId}/prepared-items/${itemId}`, payload, {
            withCredentials: true,
          })
        ),

      deletePreparedItem: (
        requestId: string,
        itemId: string,
        version: number
      ): Promise<ApiResponse<void>> =>
        unwrap<ApiResponse<void>>(
          http.delete(`/v1/admin/prescriptions/${requestId}/prepared-items/${itemId}`, {
            params: { version },
            withCredentials: true,
          })
        ),

      reopenReview: (requestId: string): Promise<ApiResponse<PrescriptionRequestDto>> =>
        unwrap<ApiResponse<PrescriptionRequestDto>>(
          http.post(`/v1/admin/prescriptions/${requestId}/reopen-review`, {}, {
            withCredentials: true,
          })
        ),
    },

    /**
     * Admin product search (staff session cookie auth)
     * GET /v1/admin/products
     */
    products: {
      search: (params?: AdminProductSearchParams): Promise<AdminProductListResponseDto> =>
        unwrap<AdminProductListResponseDto>(
          http.get('/v1/admin/products', { params, withCredentials: true })
        ),
    },

    /**
     * Admin store settings (staff session cookie auth)
     * Path: /v1/admin/settings*
     */
    settings: {
      get: (): Promise<AdminSettingsDto> =>
        unwrap<AdminSettingsDto>(
          http.get('/v1/admin/settings', { withCredentials: true })
        ),

      update: (payload: AdminSettingsDto): Promise<AdminSettingsDto> =>
        unwrap<AdminSettingsDto>(
          http.put('/v1/admin/settings', payload, { withCredentials: true })
        ),

      uploadLogo: (file: File): Promise<ApiResponse<AdminAssetUploadResponse>> => {
        const form = new FormData()
        form.append('file', file)
        return unwrap<ApiResponse<AdminAssetUploadResponse>>(
          http.post('/v1/admin/settings/assets/logo', form, {
            headers: { 'Content-Type': 'multipart/form-data' },
            withCredentials: true,
          })
        )
      },

      uploadBanner: (file: File): Promise<ApiResponse<AdminAssetUploadResponse>> => {
        const form = new FormData()
        form.append('file', file)
        return unwrap<ApiResponse<AdminAssetUploadResponse>>(
          http.post('/v1/admin/settings/assets/banner', form, {
            headers: { 'Content-Type': 'multipart/form-data' },
            withCredentials: true,
          })
        )
      },
    },
  },

  /**
   * Store onboarding endpoints
   * Path: /v1/onboarding/*
   */
  onboarding: {
    /**
     * Check onboarding status for a store (public)
     * GET /v1/onboarding/{storeId}/status?invoice={invoiceNumber}
     */
    checkStatus: (storeId: string, invoiceNumber: string): Promise<ApiResponse<EcommerceOnboardingSummary>> =>
      unwrap<ApiResponse<EcommerceOnboardingSummary>>(
        http.get(`/v1/onboarding/${storeId}/status`, { params: { invoice: invoiceNumber } })
      ),

    /**
     * Activate / create a store (authenticated, JSON-only)
     * POST /v1/onboarding/{storeId}/{invoiceNumber}/activate
     */
    activate: (storeId: string, invoiceNumber: string, payload: StoreDetailDto): Promise<ApiResponse<StoreDetailDto>> =>
      unwrap<ApiResponse<StoreDetailDto>>(
        http.post(`/v1/onboarding/${storeId}/${invoiceNumber}/activate`, payload)
      ),

    /**
     * Activate / create a store with optional logo and banner images (multipart)
     * POST /v1/onboarding/{storeId}/{invoiceNumber}/activate
     * Parts: store (JSON blob), logo (file, optional), banner (file, optional)
     */
    activateWithImages: (
      storeId: string,
      invoiceNumber: string,
      payload: StoreDetailDto,
      logoFile?: File | null,
      bannerFile?: File | null
    ): Promise<ApiResponse<StoreDetailDto>> => {
      const formData = new FormData()
      formData.append(
        'store',
        new Blob([JSON.stringify(payload)], { type: 'application/json' })
      )
      if (logoFile) formData.append('logo', logoFile)
      if (bannerFile) formData.append('banner', bannerFile)
      return unwrap<ApiResponse<StoreDetailDto>>(
        http.post(`/v1/onboarding/${storeId}/${invoiceNumber}/activate`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
      )
    },
  },
}

export default api
